import { useEffect, useState } from 'react';
import axios from 'axios';
import { Check, AlertTriangle, Pill, Sparkles, ArrowRight, ArrowLeft } from 'lucide-react';
import { TYPE_LABELS, TYPE_REASONS, TYPE_COLORS, WELLNESS_SUITE } from './data';
import ProductBottle from './ProductBottle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublicPlanScreen = () => {
  const planId = window.location.pathname.split('/plan/')[1] || '';
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!planId) {
      setError('Missing plan ID');
      return;
    }
    axios.get(`${API}/kiosk/plan/${planId}`)
      .then((r) => setPlan(r.data))
      .catch(() => setError('Plan not found or expired.'));
  }, [planId]);

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-6 text-center"
        style={{ backgroundColor: '#F6F4F0', fontFamily: "'Manrope', sans-serif" }}
      >
        <div>
          <div className="text-5xl mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>·</div>
          <div className="text-lg text-[#0d1f15] font-bold mb-2">{error}</div>
          <div className="text-sm text-[#5a7062]">Please head back to the kiosk to generate a new plan.</div>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-[#5a7062]"
        style={{ backgroundColor: '#F6F4F0', fontFamily: "'Manrope', sans-serif" }}
      >
        Loading your plan…
      </div>
    );
  }

  const products = plan.products || [];
  const insights = plan.insights || [];

  return (
    <div
      className="min-h-screen text-[#0d1f15]"
      style={{
        backgroundColor: '#F6F4F0',
        backgroundImage:
          'radial-gradient(40rem 30rem at 0% -10%, rgba(31,61,43,0.08), transparent 60%),'
          + ' radial-gradient(35rem 25rem at 100% 110%, rgba(217,119,54,0.07), transparent 65%)',
      }}
    >
      <div className="max-w-xl mx-auto px-5 sm:px-8 py-10">
        {/* Home link */}
        <div className="mb-6">
          <a
            href="/"
            data-testid="plan-home-link"
            className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.15em] text-[#5a7062] hover:text-[#1f3d2b] transition-colors"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            <ArrowLeft size={12} strokeWidth={2.5} /> montclairvitamins.com
          </a>
        </div>
        <header className="text-center mb-8">
          <img
            src="/logo.png"
            alt="Healthy Life Vitamins"
            className="h-14 sm:h-16 mx-auto object-contain mb-3 drop-shadow-sm"
          />
          <div className="text-[10px] uppercase tracking-[0.4em] font-bold text-[#1f3d2b]/70 mb-5 flex items-center justify-center gap-3">
            <span className="block w-6 h-px bg-[#1f3d2b]/30" />
            Wellness Club
            <span className="block w-6 h-px bg-[#1f3d2b]/30" />
          </div>

          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1f3d2b] text-white text-[10px] font-black uppercase tracking-[0.25em] mb-4">
            <Check size={12} strokeWidth={3} /> Saved Plan
          </div>
          <h1
            className="text-4xl sm:text-5xl text-[#0d1f15] mb-2 leading-[1.05] tracking-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
          >
            Your Vitamin Plan
          </h1>
          <p
            className="text-sm sm:text-base text-[#5a7062]"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Show this in store and a wellness advisor will help you find the
            right products.
          </p>
        </header>

        {insights.length > 0 && (
          <section
            data-testid="public-plan-insights"
            className="mb-8 bg-white rounded-3xl p-5 sm:p-6 border border-[#1f3d2b]/10 shadow-sm"
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-[#1f3d2b]/10 flex items-center justify-center">
                <Pill size={14} className="text-[#1f3d2b]" strokeWidth={2} />
              </div>
              <h3
                className="text-lg text-[#1f3d2b] tracking-tight"
                style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800 }}
              >
                Medication Insights
              </h3>
            </div>
            <ul className="space-y-3 mb-3">
              {insights.map((ins, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span
                    className="flex-shrink-0 w-2 h-2 rounded-full mt-2"
                    style={{ background: TYPE_COLORS[ins.type] || '#1f3d2b' }}
                  />
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[#5a7062] mb-0.5">
                      {ins.medication_label}
                    </div>
                    <div
                      className="text-[#0d1f15] leading-snug text-sm"
                      style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700 }}
                    >
                      May lower your {ins.nutrient.toLowerCase()}
                    </div>
                    <div className="text-xs text-[#5a7062] mt-0.5">{ins.reason}</div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="flex items-start gap-2 text-[11px] text-[#5a7062] bg-[#f5e6d0] border border-[#d97736]/30 rounded-xl px-3 py-2">
              <AlertTriangle size={12} className="flex-shrink-0 mt-0.5 text-[#d97736]" strokeWidth={2.5} />
              <span>
                General information, not medical advice. Discuss new supplements
                with your physician or pharmacist.
              </span>
            </div>
          </section>
        )}

        {products.length === 0 ? (
          <div className="text-center text-[#5a7062] py-12">
            No products were saved on this plan.
          </div>
        ) : (
          <section className="space-y-4">
            {products.map(({ type, name, isLive }, i) => {
              const isWellnessSuite = type === 'wellness_suite';
              const color = isWellnessSuite ? WELLNESS_SUITE.color : (TYPE_COLORS[type] || '#1f3d2b');
              return (
              <div
                key={`${type}-${i}`}
                data-testid={`public-plan-${type}`}
                className="bg-white rounded-3xl p-5 border shadow-sm flex items-stretch gap-4"
                style={{ borderColor: isWellnessSuite ? '#d9773620' : 'rgba(31,61,43,0.08)' }}
              >
                <div
                  className="flex-shrink-0 rounded-2xl flex items-center justify-center self-start"
                  style={{
                    background: `${color}10`,
                    width: 84, height: 84,
                  }}
                >
                  {isWellnessSuite ? (
                    <svg viewBox="0 0 60 60" width={60} height={60} aria-hidden="true" fill="none">
                      <circle cx="30" cy="30" r="25" stroke="#d97736" strokeWidth="1.5" opacity="0.25" />
                      <path d="M30 10 C21 17, 15 24, 19 33 C23 42, 37 42, 41 33 C45 24, 39 17, 30 10Z" fill="#d97736" opacity="0.15" stroke="#d97736" strokeWidth="1.5" opacity2="0.7" />
                      <line x1="30" y1="10" x2="30" y2="42" stroke="#d97736" strokeWidth="1.5" opacity="0.35" strokeLinecap="round" />
                      <circle cx="30" cy="30" r="3" fill="#d97736" opacity="0.9" />
                    </svg>
                  ) : (
                    <ProductBottle color={color} size={60} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className="inline-block text-[9px] font-black uppercase tracking-[0.25em] mb-1.5 px-2 py-0.5 rounded-full"
                    style={{
                      background: `${color}15`,
                      color,
                    }}
                  >
                    {isWellnessSuite ? 'Wellness Suite' : (TYPE_LABELS[type] || type)}
                  </div>
                  <div
                    className="text-base text-[#0d1f15] leading-snug mb-1 tracking-tight break-words"
                    style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700 }}
                  >
                    {name}
                  </div>
                  <div
                    className="text-xs text-[#5a7062] leading-relaxed"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    {isWellnessSuite ? WELLNESS_SUITE.description : (TYPE_REASONS[type] || '')}
                  </div>
                  {isWellnessSuite ? (
                    <div className="mt-2 flex items-center gap-3">
                      <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.2em] text-[#d97736]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#d97736]" /> {WELLNESS_SUITE.price_label}
                      </span>
                      <a
                        href={WELLNESS_SUITE.booking_url}
                        data-testid="public-plan-wellness-book-cta"
                        className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.2em] text-white px-2.5 py-1 rounded-full"
                        style={{ background: '#d97736' }}
                      >
                        Book <ArrowRight size={10} strokeWidth={2.5} />
                      </a>
                    </div>
                  ) : (
                    isLive && (
                      <div className="mt-2 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.2em] text-[#1f3d2b]/60">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#1f3d2b]" /> In stock today
                      </div>
                    )
                  )}
                </div>
              </div>
              );
            })}
          </section>
        )}

        <div className="mt-10 bg-white/60 backdrop-blur-md rounded-2xl p-5 border-l-4 border-[#d97736]">
          <div className="flex items-center gap-2 text-[#1f3d2b] mb-1">
            <Sparkles size={16} strokeWidth={2.5} />
            <h3
              className="text-base tracking-tight"
              style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800 }}
            >
              In-store next steps
            </h3>
          </div>
          <p
            className="text-sm text-[#5a7062] leading-relaxed"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Bring this list to a wellness advisor — they&apos;ll confirm the
            right brand and dosage and check for any conflicts with your
            current routine.
          </p>
        </div>

        <div
          className="mt-10 text-center text-[10px] uppercase tracking-[0.3em] text-[#5a7062]"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          Healthy Life Vitamins · Plan {plan.id}
        </div>
      </div>
    </div>
  );
};

export default PublicPlanScreen;
